/**
 * Guide engine v3 - engine UI strings (#1029, epic #1020). Spec R9.
 *
 * Engine-chrome copy (button fallbacks, cue fallback, error text, aria labels,
 * the sandbox-preparing label) translated per locale. Source of truth is
 * `./engine-strings/en-US.json` (authored, flat v3 keys matching
 * DEFAULT_GUIDE_STRINGS); other locales are generated by
 * `scripts/publish-engine-strings.py` and committed alongside it. Vite eagerly
 * bundles every JSON in the directory, so engine chrome needs no network call
 * and has no fetch-failure path.
 *
 * Fallback chain (R9): locale map -> en-US -> the overlay's built-in defaults
 * (the consumer merges over DEFAULT_GUIDE_STRINGS), so a missing locale or key
 * renders English rather than a blank label.
 */

const modules = import.meta.glob('./engine-strings/*.json', {
  eager: true,
  import: 'default',
});

const STRINGS = {};
for (const [path, mod] of Object.entries(modules)) {
  const match = path.match(/\/([a-z]{2}-[A-Z]{2})\.json$/);
  if (match) STRINGS[match[1]] = mod;
}

export const FALLBACK_LOCALE = 'en-US';
const FALLBACK_STRINGS = STRINGS[FALLBACK_LOCALE] || {};

export const availableEngineLocales = () => Object.keys(STRINGS);

/**
 * The engine-string map for a locale: en-US filled in under the locale's own
 * keys, so a partially generated locale never drops a label.
 * @param {string} locale
 * @returns {Object} flat key -> string map
 */
export const getEngineStrings = (locale) => {
  const map = STRINGS[locale];
  if (!map || locale === FALLBACK_LOCALE) return FALLBACK_STRINGS;
  return { ...FALLBACK_STRINGS, ...map };
};
